import { useContext, useEffect, useReducer, useState } from "react";
import { useLocation } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import ActionButton from "../components/formInputs/buttons/ActionButton";
import BreadCrumb from "../components/breadCrumb/BreadCrumb";
import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";
import EprStudentInfoBlock from "../components/studentInfoBlock/EprStudentInfoBlock";
import FormReducer from "../utils/FormReducer";
import GuardianEprDao from "../dao/GuardianEprDao";
import LoadingSvg from "../components/loadingSvg/LoadingSvg";
import RbA from "../components/rba/RbA";
import StudentAutoSuggest from "../components/formInputs/autoSuggest/StudentAutoSuggest";

import { EVALUATION_SITE } from "../utils/auth/config";
import { pickEprForMostRecentPdf } from "../utils/pickEprForMostRecentPdf";
import { GlobalContext } from "../components/contextProvider/ContextProvider";

import "../styles/EprLinks.scss";

const StudentSearch = () => {
    const { dispatch, state } = useContext(GlobalContext);
    const { schoolYearDto, token, userDetails } = state;

    const initialFormState = {
        studentSearch: "",
        firstName: "",
        lastName: "",
        studentNumber: ""
    };
    const [formState, formDispatch] = useReducer(FormReducer, initialFormState);

    const [gradingProfiles, setGradingProfiles] = useState(null);
    const [hasPredominateSchool, setHasPredominateSchool] = useState(true);
    const [loader, setLoader] = useState(false);
    const [mostRecentEpr, setMostRecentEpr] = useState(null);
    const [mrEprGrade, setMrEprGrade] = useState("");
    const [resultMessages, setResultMessages] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentSearchResults, setStudentSearchResults] = useState(null);

    const location = useLocation();
    const { pathname } = location;

    const allowedRolesArray = ["ASSISTANT_PRINCIPAL", "EPR_ADMIN", "PK12", "PRINCIPAL", "REGISTRAR", "SDIS", "TEACHER"];

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (!value || value === "-") {
            clearInput();
            if (name === "studentSearch") {
                setLoader(false);
                setResultMessages([]);
                setStudentSearchResults(null);
                setGradingProfiles(null);
            }
        }

        formDispatch({
            type: "text",
            field: name,
            payload: value
        });
    };

    /**
     * This is resetting formState, same as resetAutoSuggest in StudentAutoSuggest.jsx
     */
    const clearInput = () => {
        formDispatch({
            type: "reset",
            payload: { ...initialFormState }
        });
    };

    /**
     * Check to see if the user has a predominate school location
     * If not, show the dialog
     */
    useEffect(() => {
        if (userDetails) {
            const { userAttributeDto } = userDetails;
            const { userAttributeMap } = userAttributeDto;

            if (!userAttributeMap.CURRENT_PREDOMINANT_SCHOOL) {
                setHasPredominateSchool(false);
            }
        }
    }, [userDetails]);

    /**
     * Resetting formState
     */
    useEffect(() => {
        if (formState.studentSearch === "") {
            setGradingProfiles(null);
            setMostRecentEpr(null);
            setMrEprGrade("");
            formDispatch({
                type: "reset",
                payload: { ...initialFormState }
            });
        }
        /* eslint-disable-next-line */
    }, [formState.studentSearch]);

    /**
     * Clear prior report-card panels when a different student is selected.
     */
    useEffect(() => {
        setGradingProfiles(null);
        setMostRecentEpr(null);
        setMrEprGrade("");
        setResultMessages([]);
    }, [formState.studentNumber]);

    // helper to convert blob to text
    const blobToText = async (blob) => {
        return await new Response(blob).text();
    };

    const downloadPdf = async (key, schoolYearName) => {
        try {
            const options = {
                action: "eprPdfDownload",
                gradingProfileId: key,
                studentNumber: selectedStudent.studentNumber,
                token
            };
            setLoader(true);
            const response = await ElementaryProgressReportDao(options);
            if (response) {
                // This will open the PDF in a new tab AND download the file simultaneously
                const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                window.open(url, "_blank");
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                    "download",
                    `epr_${selectedStudent?.lastName}_${selectedStudent?.firstName}_${selectedStudent.studentNumber}.pdf`
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
                setResultMessages((prevState) => {
                    return [
                        {
                            error: false,
                            message: `${schoolYearName} Report Card PDF retrieved and downloaded successfully for the student..`
                        },
                        ...(prevState || [])
                    ];
                });
            }
        } catch (error) {
            if (error.response && error.response.data instanceof Blob) {
                const text = await blobToText(error.response.data);
                setResultMessages((prevState) => {
                    return [
                        {
                            error: true,
                            message: `Sorry, we are unable to retrieve the Report Card PDF for the student ${schoolYearName}. Please provide the following information when contacting the district for support:\n${text}`
                        },
                        ...(prevState || [])
                    ];
                });
            }
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (!formState?.studentNumber || !schoolYearDto || !token) {
            return;
        }

        let isMounted = true;

        const fetchData = async () => {
            // Check EPR Status
            let statusPayload = false;
            try {
                const statusOptions = {
                    action: "eprStatusRead",
                    schoolYearKey: schoolYearDto.key,
                    studentNumber: formState.studentNumber,
                    token
                };
                const statusResponse = await GuardianEprDao(statusOptions);

                if (statusResponse?.data?.payload) {
                    statusPayload = statusResponse.data.payload;
                }
            } catch (error) {
                console.error("Unable to retrieve EPR status:\n", error);
                statusPayload = false;
            }

            // Fetch EPRs (Only if statusPayload is true)
            if (isMounted && statusPayload) {
                try {
                    const eprsOptions = {
                        action: "eprGradingProfileRead",
                        schoolYearKey: schoolYearDto.key,
                        studentNumber: formState.studentNumber,
                        token
                    };
                    const eprsResponse = await GuardianEprDao(eprsOptions);
                    if (isMounted && eprsResponse?.data) {
                        const { payload } = eprsResponse.data;
                        if (payload && payload.length > 0) {
                            const eprForPdf = pickEprForMostRecentPdf(payload);
                            if (eprForPdf) {
                                setMostRecentEpr(eprForPdf);
                                let grade = eprForPdf.gradeName;
                                if (
                                    ["K", "Kindergarten", "K_FULL", "K_HALF", "K_HALF_AM", "K_HALF_PM"].includes(grade)
                                ) {
                                    grade = "Kindergarten";
                                } else {
                                    grade = `${grade} Grade`;
                                }
                                setMrEprGrade(grade);
                            }
                        }
                    }
                } catch (error) {
                    console.error("Unable to retrieve most recent EPR:\n", error);
                    if (isMounted) {
                        setMostRecentEpr(null);
                    }
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [schoolYearDto, formState?.studentNumber, token]);

    useEffect(() => {
        if (gradingProfiles || !schoolYearDto || !formState.studentNumber || !token) {
            return;
        }

        /**
         * @name fetchEprArchive
         * @description Check if the student has an EPR archive
         * @param {{}} app
         * @returns {{}} updatedApp
         */
        const fetchEprArchive = async () => {
            try {
                const eprOptions = {
                    action: "archiveGradingProfilesRead",
                    schoolYearKey: schoolYearDto.key,
                    studentNumber: formState.studentNumber,
                    token
                };
                const response = await ElementaryProgressReportDao(eprOptions);
                if (response && response?.data) {
                    const { payload } = response.data;
                    payload?.sort((a, b) => {
                        return parseInt(a.key, 10) < parseInt(b.key, 10) ? 1 : -1;
                    });
                    if (payload && payload.length > 0) {
                        setGradingProfiles(Object.values(payload));
                    }
                }

                return true;
            } catch (error) {
                console.error("Unable to retrieve EPR Archive:\n", error);
            }
        };

        if (!gradingProfiles) {
            fetchEprArchive();
        }
    }, [dispatch, gradingProfiles, schoolYearDto, formState?.studentNumber, token]);

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
            {!pathname.includes("/home") && (
                <BreadCrumb
                    labelObj={[
                        { label: "Home", path: "/home" },
                        { label: "Student Search", path: "/student-search" }
                    ]}
                />
            )}
            <div className="gutter-95">
                <h6>Use this tool to search for students and to view or download their Archived Report Cards</h6>
                {hasPredominateSchool ? (
                    <div className="grey-background">
                        <div className="student-search-container">
                            <div className="student-search">
                                <StudentAutoSuggest
                                    formDispatch={formDispatch}
                                    formState={formState}
                                    handleChange={handleChange}
                                    id="student-search"
                                    label="Student Search:"
                                    name="studentSearch"
                                    setResultMessages={setResultMessages}
                                    selectedStudent={selectedStudent}
                                    setLoader={setLoader}
                                    setSelectedStudent={setSelectedStudent}
                                    setStudentSearchResults={setStudentSearchResults}
                                    studentSearchResults={studentSearchResults}
                                />
                            </div>
                        </div>
                        {selectedStudent && (
                            <>
                                <hr />
                                <EprStudentInfoBlock setLoader={setLoader} studentInfo={selectedStudent} />
                            </>
                        )}
                        {selectedStudent && (
                            <div className="display-container">
                                <div className="epr-links-container">
                                    <h5 className="ms-3">Most Recent Report Card PDF</h5>
                                    {mostRecentEpr ? (
                                        <OverlayTrigger
                                            placement="top"
                                            overlay={
                                                mostRecentEpr.pdfProcessed !== "Y" ? (
                                                    <Tooltip id={`pdf-tooltip-${selectedStudent.key}`}>
                                                        Report Card PDF is not ready. Please check back soon.
                                                    </Tooltip>
                                                ) : (
                                                    /* eslint-disable-next-line react/jsx-no-useless-fragment */
                                                    <></>
                                                )
                                            }
                                        >
                                            <div className="spare-div">
                                                <ActionButton
                                                    className="action-button-reg epr-link"
                                                    disable={mostRecentEpr.pdfProcessed !== "Y"}
                                                    label={`${mostRecentEpr.schoolYearName} -- ${mrEprGrade} -- ${mostRecentEpr.teacherName}`}
                                                    onClick={() => {
                                                        downloadPdf(
                                                            mostRecentEpr.gradingProfileId,
                                                            mostRecentEpr.schoolYearName
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </OverlayTrigger>
                                    ) : (
                                        <p className="ms-3">Most Recent Report Card unavailable for the student.</p>
                                    )}
                                    <h5 className="ms-3 mt-4">Archived Report Card PDFs</h5>
                                    {gradingProfiles ? (
                                        gradingProfiles?.map((obj) => {
                                            const { gradeName, key, schoolYearName, teacherName } = obj;
                                            let grade = gradeName;
                                            if (
                                                [
                                                    "K",
                                                    "Kindergarten",
                                                    "K_FULL",
                                                    "K_HALF",
                                                    "K_HALF_AM",
                                                    "K_HALF_PM"
                                                ].includes(gradeName)
                                            ) {
                                                grade = "Kindergarten";
                                            } else {
                                                grade = `${gradeName} Grade`;
                                            }
                                            return (
                                                <ActionButton
                                                    className="action-button-reg epr-link"
                                                    key={obj.key}
                                                    label={`${schoolYearName} -- ${grade} -- ${teacherName}`}
                                                    onClick={() => {
                                                        downloadPdf(key, schoolYearName);
                                                    }}
                                                />
                                            );
                                        })
                                    ) : (
                                        <p className="ms-3">No historical Report Cards found for the student.</p>
                                    )}
                                </div>

                                <div className="results-container">
                                    {resultMessages &&
                                        resultMessages.map((message, index) => {
                                            return (
                                                <p key={index} className={message.error ? "error" : "success"}>
                                                    {message.message}
                                                </p>
                                            );
                                        })}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        style={{
                            border: "1px solid black",
                            borderRadius: "5px",
                            padding: "12px 24px",
                            margin: "24px 0px",
                            width: "fit-content"
                        }}
                    >
                        <h5>No Location Found</h5>
                        <div>A primary location is required before you can access Report Card.</div>
                        <div>
                            Please update your employee profile{" "}
                            <a href={`${EVALUATION_SITE}/profile`} target="_blank" rel="noreferrer">
                                here.
                            </a>
                        </div>
                    </div>
                )}
                {loader && <LoadingSvg />}
            </div>
        </RbA>
    );
};

export default StudentSearch;
